/** @format */

import React, { useCallback, useEffect, useState } from "react";
import AuthFlag from "../../auth/AuthFlag";
import { Form, Button, Alert } from "react-bootstrap";
import { useLocale } from "../../../i18n/hooks";
import { useTranslation } from "react-i18next";
import { Subject } from "../../../app/services/subjectApi";
import HelperTooltip from "../helperTooltip/HelperTooltip";
import { isSecureLink } from "../../../utils/functions";
import { BrandingOption, fundedBy, Qualifications } from "../../../types";
import SpecAccordion from "../specAccordion/SpecAccordion";
import { LocalText } from "../BLResourceTypes";
import SubjectInput from "../../SubjectInput/SubjectInput";
import { Markup } from "interweave";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";

export type ResourceForm = {
  code: string;
  qualification: string;
  subject: Subject | undefined;
  levels: string[];
  specItem: string;
  acknowledgements: string;
  fundedBy: fundedBy;
  author: string;
  resourceTitle: LocalText;
  branding: BrandingOption;
};

const defaultResourceForm: ResourceForm = {
  code: "",
  qualification: "",
  levels: [],
  subject: { title: { en: "", cy: "" }, id: "" },
  specItem: "",
  acknowledgements: "",
  fundedBy: "N/A", // this does not select N/A on the form
  author: "",
  resourceTitle: { en: "", cy: "" },
  branding: "cbac&wjec",
};

type Props = {
  propDefaultResourceForm: ResourceForm | undefined;
  uploadFinal: (formData: ResourceForm) => void;
  buttonText: string;
};

//TODO: could the form elements be their own components? This may confuse state only being mutated in this component

/**
 * @param propDefaultResourceForm this will overwrite the current form values if defined. Should be used to initialise the form
 * @param uploadFinal a function called when the user submits the form. Gives the final form data
 * @param buttonText the text to be displayed on the submit button (translated)
 *
 * the form state is mutated only in the form component.
 * each form element handles its own mutation of the form state.
 */
const CreateResource: React.FC<Props> = ({
  propDefaultResourceForm,
  uploadFinal,
  buttonText,
}) => {
  const { t: _ } = useTranslation();

  const locale = useLocale();
  const [qualifications, setQualifications] = useState<Qualifications[]>();
  const [createResourceFormData, setCreateResourceFormData] =
    useState<ResourceForm>(defaultResourceForm); // Default set so it is not undefined

  /**
   * if a propDefaultResourceForm is given and defined, overwrite the form data to this
   */
  useEffect(() => {
    if (propDefaultResourceForm)
      setCreateResourceFormData(propDefaultResourceForm);
  }, [propDefaultResourceForm]);

  /** Check if current resource code in form is valid */
  const isValidCode = (): boolean => {
    return (
      createResourceFormData.code !== "" &&
      /^([A-Za-z0-9_-])+$/.test(createResourceFormData.code)
    );
  };

  const isValidForm = (): boolean => {
    //NOTE: split into ifs for readablity

    if (!isValidCode()) return false;

    if (createResourceFormData.qualification === "") return false;

    if (
      createResourceFormData.subject === undefined ||
      createResourceFormData.subject.id === ""
    )
      return false;

    if (
      createResourceFormData.acknowledgements !== "" &&
      !isSecureLink(createResourceFormData.acknowledgements)
    )
      return false;

    return true;
  };

  const handleSubmit = () => {
    if (!isValidForm) return; // Check again incase the button has had disabled manually removed

    uploadFinal(createResourceFormData);
  };

  useEffect(() => {
    try {
      const data = require(`../../../json/qualifications.json`);

      setQualifications(data);
    } catch {
      console.log("failed to fetch qualifications");
    }
  }, []);

  const onChangeSubject = useCallback(
    (subject: Subject) => {
      setCreateResourceFormData({
        ...createResourceFormData,
        subject: subject,
        specItem: "",
      });
    },
    [createResourceFormData],
  );

  const groupedQualifications =
    qualifications?.reduce(
      (a: { [key: string]: Qualifications[] }, c: Qualifications) => {
        // Grab group to use as dictionary key
        let k: string = c.group ?? "other";

        // Build dictionary
        if (a[k]) a[k].push(c);
        else a[k] = [c];

        return a;
      },
      {},
    ) ?? {};

  return (
    <AuthFlag authGroup={"staff"} fallback={<h1>{_("Unauthorised")}</h1>}>
      <div className="d-flex flex-column gap-3">
        <Form.Group>
          <h5 className="mt-4">
            <span style={{ color: "red" }}>*</span> {_("Code")}
            <HelperTooltip tooltipText={_("formattingCodeInformation")} />
          </h5>
          <Form.Control
            name="code"
            className="mb-2"
            role="input"
            aria-label="Resource code"
            required
            isInvalid={!isValidCode()}
            value={createResourceFormData.code}
            onChange={(e) => {
              setCreateResourceFormData({
                ...createResourceFormData,
                code: e.target.value,
              });
            }}
          />
          {createResourceFormData.code.startsWith("mfg") && (
            <Alert variant="danger">
              <ExclamationTriangle
                className="text-danger"
                style={{ verticalAlign: "-0.125em" }}
              />
              &nbsp;
              <Markup
                content={_(
                  "Are you sure you want to create a resource with the code <code>mfg</code> instead of <code>mfw</code>?",
                )}
              />
            </Alert>
          )}
        </Form.Group>

        <Form.Group>
          <h5>{_("Resource Title")}</h5>
          <Form.Control
            name="resourceTitle"
            className="mb-2"
            role="input"
            aria-label="Resource Title"
            value={createResourceFormData.resourceTitle[locale]}
            onChange={(e) => {
              setCreateResourceFormData({
                ...createResourceFormData,

                resourceTitle: {
                  ...createResourceFormData.resourceTitle,
                  [locale]: e.target.value,
                },
              });
            }}
          />
        </Form.Group>

        <Form.Group>
          <h5>
            <span style={{ color: "red" }}>*</span> {_("Qualification")}
          </h5>
          <Form.Select
            className="text-monospace mb-2"
            size="sm"
            required
            value={createResourceFormData.qualification}
            isInvalid={createResourceFormData.qualification === ""}
            onChange={(e) => {
              let _qualification = qualifications?.find(
                (qual) => qual.code === e.currentTarget.value,
              );

              setCreateResourceFormData({
                ...createResourceFormData,
                qualification: e.currentTarget.value,
                specItem: "", // Overwrite old value
                levels: _qualification?.keyStage ?? [], // Overwrite old value
              });
            }}
            style={{ width: "auto" }}
            role="select"
            aria-label="Qualification select"
            name="qualification"
          >
            <option hidden>{_("Select the qualification")}</option>
            {groupedQualifications &&
              Object.keys(groupedQualifications).map((group: string) => (
                <optgroup key={group} label={_(group)}>
                  {groupedQualifications[group].map(
                    (qualification: Qualifications, index: number) => (
                      <option
                        key={`${qualification.code}-${index}`}
                        value={qualification.code}
                      >
                        {`${qualification.title.en} / ${qualification.title.cy}`}
                      </option>
                    ),
                  )}
                </optgroup>
              ))}
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <h5>
            {_("Key Stages")}
            <HelperTooltip tooltipText={_("keystageInformation")} />
          </h5>
          {["KS2", "KS3", "KS4", "KS5"].map(
            (keystage: string, index: number) => (
              <Form.Check
                key={`${keystage}-${index}`}
                label={`${keystage}`}
                name="levels"
                type="checkbox"
                id={`keystage-${keystage}`}
                checked={createResourceFormData.levels.includes(keystage)}
                disabled={
                  qualifications
                    ?.find(
                      (qual) =>
                        qual.code === createResourceFormData.qualification,
                    )
                    ?.keyStage?.includes(keystage) ?? false
                }
                value={`${keystage}`}
                onChange={(e) => {
                  if (
                    e.target.checked &&
                    !createResourceFormData.levels.includes(keystage)
                  ) {
                    setCreateResourceFormData({
                      ...createResourceFormData,
                      levels: [...createResourceFormData.levels, keystage],
                    });
                  } else if (
                    !e.target.checked &&
                    createResourceFormData.levels.includes(keystage)
                  ) {
                    let newKeystage = createResourceFormData.levels;
                    newKeystage.splice(newKeystage.indexOf(keystage), 1);

                    setCreateResourceFormData({
                      ...createResourceFormData,
                      levels: newKeystage,
                    });
                  }
                }}
              />
            ),
          )}
        </Form.Group>

        <SubjectInput
          value={createResourceFormData.subject}
          onChange={onChangeSubject}
        />

        <Form.Group>
          <h5>{_("Branding")}</h5>
          <Form.Select
            name="branding"
            value={
              createResourceFormData.branding === ""
                ? "cbac&wjec"
                : (createResourceFormData.branding ?? "")
            }
            onChange={(e) =>
              setCreateResourceFormData({
                ...createResourceFormData,
                branding: e.target.value as BrandingOption,
              })
            }
          >
              <option value="" disabled>{_("Select a brand")}</option>
              <option value="cbac&wjec">WJEC & CBAC ({_("Dual language")})</option>
              <option value="cbac">CBAC ({_("Welsh only")})</option>
              <option value="wjec">WJEC ({_("English only")})</option>
              <option value="eduqas">Eduqas</option>
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <h5>
            {_("Spec item")}
            <HelperTooltip tooltipText={_("specItemInformation")} />
          </h5>
          <SpecAccordion
            handleSelectedChange={(e) => {
              setCreateResourceFormData({
                ...createResourceFormData,
                specItem: e.target.value,
              });
            }}
            SubjectId={createResourceFormData.subject?.id ?? ""}
            Qualification={createResourceFormData.qualification}
            DefaultSelected=""
          />
        </Form.Group>

        <Form.Group>
          <h5>
            {_("Acknowledgements")}
            <HelperTooltip tooltipText={_("acknowledgementsInformation")} />
          </h5>
          <Form.Control
            name="acknowledgements"
            className="mb-2"
            role="input"
            aria-label="Resource acknowledgements input"
            onChange={(e) => {
              setCreateResourceFormData({
                ...createResourceFormData,
                acknowledgements: e.target.value,
              });
            }}
            value={createResourceFormData.acknowledgements}
            isInvalid={
              createResourceFormData.acknowledgements !== "" &&
              !isSecureLink(createResourceFormData.acknowledgements)
            }
          />
          <Form.Control.Feedback type="invalid">
            {_("Please enter a secure link (https)")}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group>
          <h5>
            {_("Funded By")}
            <HelperTooltip tooltipText={_("fundedByInformation")} />
          </h5>
          {(['N/A', 'Adnodd', 'WelshGov'] as fundedBy[]).map((value) => (
            <Form.Check
              inline
              label={_(value)}
              type="radio"
              name="fundedBy"
              value={value}
              checked={createResourceFormData.fundedBy === value}
              onChange={(e) => 
                setCreateResourceFormData({
                  ...createResourceFormData,
                  fundedBy: e.target.value as fundedBy,
                })
              }
            />
          ))}
        </Form.Group>

        <Form.Group>
          <h5>
            {_("Author")}
            <HelperTooltip tooltipText={_("authorInformation")} />
          </h5>
          <Form.Control
            name="author"
            className="mb-2"
            role="input"
            aria-label="Authors input"
            value={createResourceFormData.author}
            onChange={(e) =>
              setCreateResourceFormData({
                ...createResourceFormData,
                author: e.target.value,
              })
            }
          />
        </Form.Group>

        {
          //NOTE: I have chosen this way instead of using a form submit because at the
          // time of writing this code, this form is inside another form, so this way avoids form nesting.
        }
        <Button onClick={() => handleSubmit()} disabled={!isValidForm()}>
          {buttonText}
        </Button>
      </div>
    </AuthFlag>
  );
};

export default CreateResource;
